import React from "react";

export type ButtonVariant = "primary" | "secondary" | "green" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

export interface YhButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** primary = blue fill | secondary = white / back style */
  variant?: ButtonVariant;
  /** alias for variant — pass "primary" or "secondary" */
  color?: "primary" | "secondary";
  size?: ButtonSize;
  loading?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  fullWidth?: boolean;
}

const variantClass: Record<ButtonVariant, string> = {
  primary: "yh-btn-primary",
  secondary: "yh-btn-secondary",
  green: "yh-btn-green",
  ghost:
    "inline-flex min-w-[110px] items-center justify-center gap-2 rounded-xl border border-transparent bg-transparent px-7 py-3 text-sm font-medium text-[#841C39] transition-colors hover:bg-[#F9F0F4]",
  danger:
    "inline-flex min-w-[110px] items-center justify-center gap-2 rounded-xl border border-[#FF1717] bg-[#FF1717] px-7 py-3 text-sm font-semibold text-white transition-colors hover:opacity-90",
};

const sizeClass: Record<ButtonSize, string> = {
  sm: "!min-w-[96px] !py-2.5 !px-5 !text-sm",
  md: "!min-w-[110px] !py-3 !px-7 !text-sm",
  lg: "!min-w-[128px] !py-3.5 !px-8 !text-base",
};

const Spinner = () => (
  <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent" />
);

/**
 * Common button — pass `variant` or `color`:
 * - primary → solid blue (#841C39) + white text
 * - secondary → white + gray border + gray text (Back / Discard)
 */
export const YhButton: React.FC<YhButtonProps> = ({
  variant,
  color,
  size = "md",
  loading = false,
  startIcon,
  endIcon,
  fullWidth = false,
  className = "",
  children,
  disabled,
  type = "button",
  ...rest
}) => {
  const resolved: ButtonVariant = variant || color || "primary";

  return (
    <button
      type={type}
      disabled={disabled || loading}
      className={`${variantClass[resolved]} ${sizeClass[size]} ${fullWidth ? "w-full" : ""
        } ${className}`}
      {...rest}
    >
      {loading ? <Spinner /> : startIcon}
      {children}
      {!loading && endIcon}
    </button>
  );
};

export default YhButton;
